import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminApi, type AdminUser } from "../api/admin";
import { UsersV2AdminApi } from "../api/users-v2";

const ROLE_OPTIONS = [
  { value: "", label: "Все роли" },
  { value: "ADMIN", label: "Администраторы" },
  { value: "MODERATOR", label: "Модераторы" },
  { value: "ORGANIZER", label: "Организаторы" },
  { value: "SUPPLIER", label: "Поставщики" },
  { value: "CUSTOMER", label: "Клиенты" },
  { value: "MANAGER_OF_SALES", label: "Менеджеры продаж" },
  { value: "GUEST", label: "Гости" },
];

const PAGE_SIZE = 25;

export const UsersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const role = searchParams.get("role") ?? "";
  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [page, setPage] = useState(0);
  const [items, setItems] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // UUID lookup for the action buttons -- legacy listUsers exposes the
  // int id, but the v2 deactivate/activate endpoints want the UUID.
  // We hydrate this lazily on demand to avoid an extra round trip per row.
  const [acting, setActing] = useState<number | null>(null);

  const heading = useMemo(() => {
    const found = ROLE_OPTIONS.find((r) => r.value === role);
    return found && role ? found.label : "Все пользователи";
  }, [role]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    AdminApi.listUsers({
      role: role || undefined,
      search: search || undefined,
      skip: page * PAGE_SIZE,
      limit: PAGE_SIZE,
    })
      .then((res) => {
        setItems(res.data.items);
        setTotal(res.data.total);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Ошибка загрузки"))
      .finally(() => setLoading(false));
  }, [role, search, page]);

  const onRoleChange = (value: string) => {
    setPage(0);
    const params = new URLSearchParams(searchParams);
    if (value) params.set("role", value);
    else params.delete("role");
    setSearchParams(params);
  };

  const onSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPage(0);
    const value = (e.currentTarget.elements.namedItem("search") as HTMLInputElement).value;
    setSearch(value);
    const params = new URLSearchParams(searchParams);
    if (value) params.set("search", value);
    else params.delete("search");
    setSearchParams(params);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-8">
      <h1 className="text-2xl font-semibold text-slate-900 mb-1">{heading}</h1>
      <p className="text-sm text-slate-500 mb-6">Всего записей: {total}</p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={role}
          onChange={(e) => onRoleChange(e.target.value)}
          className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
        >
          {ROLE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <form onSubmit={onSearchSubmit} className="flex items-center gap-2">
          <input
            type="text"
            name="search"
            defaultValue={search}
            placeholder="Поиск по email/имени"
            className="border border-slate-300 rounded-md px-3 py-2 text-sm w-72"
          />
          <button type="submit" className="px-3 py-2 bg-brand text-white rounded-md text-sm hover:opacity-90">
            Найти
          </button>
        </form>
      </div>

      {loading && <div className="py-10 text-center text-slate-500">Загрузка...</div>}
      {error && <div className="py-10 text-center text-red-600">{error}</div>}

      {!loading && !error && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">ID</th>
                <th className="text-left px-4 py-3 font-medium">Email</th>
                <th className="text-left px-4 py-3 font-medium">Имя</th>
                <th className="text-left px-4 py-3 font-medium">Роль</th>
                <th className="text-left px-4 py-3 font-medium">Город</th>
                <th className="text-left px-4 py-3 font-medium">Активен</th>
                <th className="text-left px-4 py-3 font-medium">Верифиц.</th>
                <th className="text-left px-4 py-3 font-medium">Создан</th>
                <th className="text-left px-4 py-3 font-medium">Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500">Нет данных</td></tr>
              )}
              {items.map((u) => {
                const onToggle = async () => {
                  if (!u.uuid_id) return;
                  setActing(u.id);
                  try {
                    if (u.is_active) {
                      await UsersV2AdminApi.deactivate(u.uuid_id);
                    } else {
                      await UsersV2AdminApi.activate(u.uuid_id);
                    }
                    setItems((arr) =>
                      arr.map((x) =>
                        x.id === u.id ? { ...x, is_active: !u.is_active } : x,
                      ),
                    );
                  } catch (e: any) {
                    setError(e?.response?.data?.detail || "Не удалось изменить статус");
                  } finally {
                    setActing(null);
                  }
                };
                return (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-slate-500">{u.id}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-900">{u.email}</td>
                  <td className="px-4 py-2.5">{[u.first_name, u.last_name].filter(Boolean).join(" ") || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-mono">{u.role}</span>
                  </td>
                  <td className="px-4 py-2.5">{u.city || "—"}</td>
                  <td className="px-4 py-2.5">{u.is_active ? <span className="text-emerald-600">●</span> : <span className="text-slate-400">○</span>}</td>
                  <td className="px-4 py-2.5">{u.is_verified ? <span className="text-emerald-600">●</span> : <span className="text-slate-400">○</span>}</td>
                  <td className="px-4 py-2.5 text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-2.5">
                    {u.uuid_id ? (
                      <button
                        type="button"
                        disabled={acting === u.id}
                        onClick={onToggle}
                        className={`rounded px-2.5 py-1 text-xs font-medium text-white hover:opacity-90 disabled:opacity-60 ${
                          u.is_active ? "bg-amber-600" : "bg-emerald-600"
                        }`}
                      >
                        {acting === u.id
                          ? "..."
                          : u.is_active
                            ? "Деактивировать"
                            : "Активировать"}
                      </button>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-3 py-2 border border-slate-300 rounded-md disabled:opacity-50 bg-white"
          >
            ← Назад
          </button>
          <div className="text-slate-500">Страница {page + 1} из {totalPages}</div>
          <button
            type="button"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-2 border border-slate-300 rounded-md disabled:opacity-50 bg-white"
          >
            Вперёд →
          </button>
        </div>
      )}
    </div>
  );
};
